import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Row, Col, Select, Modal, Tooltip, Form, Input, Table, Button } from 'antd';
import Loader from 'components/Common/Loader';
import MainRadioButtons from '../Common/MainRadioButtons';
import { statusRequestsData } from 'common/data/statusRequestsData';

// Zustand store
import { useShallow } from 'zustand/react/shallow';
import { useDepartmentsStore, useServiceRequestsStore } from 'storeZustand';

import { useToggle, useRequestsFields } from 'hooks';
import { StatusCell } from 'helpers';

// скрытие номера телефона через определенное время
import PhoneNumber from 'components/Common/PhoneNumber';
import { formatDate, getTimeFromDate } from 'helpers/Date/formatDate';

const ServiceRequestsTable = () => {
	const [form] = Form.useForm();
	const { TextArea } = Input;

	const [modal, toggleModal] = useToggle(false);
	const [selectedStatus, setSelectedStatus] = useState('1');
	const [selectedModalStatus, setSelectedModalStatus] = useState('1');
	console.log('selectedModalStatus', selectedModalStatus);

	// Загрузка данных департамента
	const [selectedDepartment] = useDepartmentsStore(useShallow((state) => [state.selectedDepartment]));

	// Загрузка заявок
	const [serviceRequests, updateServiceRequest, getServiceRequestById, loading, serviceRequest, getServiceRequests] =
		useServiceRequestsStore(
			useShallow((state) => [
				state.serviceRequests,
				state.updateServiceRequest,
				state.getServiceRequestById,
				state.loading,
				state.serviceRequest,
				state.getServiceRequests,
			])
		);

	const fields = useRequestsFields(serviceRequest);

	const statusData = {
		departmentId: selectedDepartment,
		selectedStatus,
	};

	// Запрос заявок со статусом по умолчанию
	useEffect(() => {
		if (selectedDepartment) {
			getServiceRequests(statusData);
		}
	}, [selectedStatus]);

	const handleRadioChange = (event) => {
		const selectedElement = event.target.value;
		setSelectedStatus(selectedElement);
	};

	const onFinish = async (values) => {
		const id = serviceRequest?.id ?? 0;

		const changedServiceRequest = {
			name: values.name,
			phone: values.phone,
			email: values.email,
			notes: values.notes,
			from: values.from,
			city: values.city,
			status: values.status,
			department: selectedDepartment,
		};

		toggleModal();

		await updateServiceRequest({ changedServiceRequest, id });
		await getServiceRequests(statusData);
	};

	// срабатывает при закрытии модалки
	const cancelFormFields = () => {
		toggleModal();
	};

	const handleStatusChange = (value) => {
		setSelectedModalStatus(value);
	};

	// Информация о конкретной заявке
	const handleCellClick = useCallback((id) => {
		getServiceRequestById(id);
		toggleModal();
	}, []);

	const columns = useMemo(
		() => [
			{
				title: 'ID',
				dataIndex: 'id',
				key: 'id',
			},
			{
				title: 'Имя',
				dataIndex: 'name',
				key: 'name',
			},
			{
				title: 'Телефон',
				dataIndex: 'phone',
				key: 'phone',
				render: (text) => <PhoneNumber phone={text} />,
			},
			{
				title: 'Email',
				dataIndex: 'email',
				key: 'email',
			},
			{
				title: 'Комментарий',
				dataIndex: 'notes',
				key: 'notes',
			},
			{
				title: 'Откуда',
				dataIndex: 'url',
				key: 'url',
				render: (text) => <div className="wordBreakBox">{text}</div>,
			},
			{
				title: 'Город',
				dataIndex: 'city',
				key: 'city',
			},
			{
				title: 'Статус',
				dataIndex: 'status',
				key: 'status',
				render: (text) => <StatusCell value={text} />,
			},
			{
				title: 'Дата',
				dataIndex: 'date_created',
				key: 'date_created',
				render: (date) => (
					<>
						<div className="text-center">{formatDate(date)}</div>
						<div className="text-center fw-bold">{getTimeFromDate(date)}</div>
					</>
				),
			},
			{
				title: 'Действия',
				key: 'actions',
				render: (text) => {
					const { id } = text;
					return (
						<div className="d-flex gap-3 users justify-content-center">
							<ul className="list-inline font-size-20 contact-links mb-0">
								<li
									className="list-inline-item text-primary cursor-pointer"
									onClick={() => {
										handleCellClick(id);
									}}
								>
									<Tooltip title="Изменить данные заявки">
										<span>
											<i className="uil uil-pen font-size-18" id={`edittooltip-${id}`} />
										</span>
									</Tooltip>
								</li>
							</ul>
						</div>
					);
				},
			},
		],
		[handleCellClick]
	);

	return (
		<>
			{/* фильтры заявок */}
			<div className="mb-2">
				<MainRadioButtons
					radioOptions={statusRequestsData}
					selectedRadio={selectedStatus}
					setSelectedRadio={setSelectedStatus}
					handleRadioChange={handleRadioChange}
				/>
			</div>
			{loading && <Loader />}
			{!loading && serviceRequests && serviceRequests?.length > 0 && (
				<Table columns={columns} dataSource={serviceRequests} pagination={false} rowKey="id" />
			)}

			<Modal title="Изменить заявку" open={modal} width={450} footer={null} onCancel={cancelFormFields}>
				<Form fields={fields} onFinish={onFinish} autoComplete="off" form={form}>
					<div className="mb-3">
						<label className="form-label">Имя</label>
						<Form.Item
							name="name"
							rules={[
								{
									required: true,
									message: 'Введите имя!',
								},
							]}
						>
							<Input />
						</Form.Item>
					</div>
					<div className="mb-3">
						<label className="form-label">Телефон</label>
						<Form.Item
							name="phone"
							rules={[
								{
									required: true,
									message: 'Введите телефон!',
								},
							]}
						>
							<Input type="number" />
						</Form.Item>
					</div>
					<div className="mb-3">
						<label className="form-label">Email</label>
						<Form.Item name="email">
							<Input />
						</Form.Item>
					</div>
					<div className="mb-3">
						<label className="form-label archi-label">Комментарий</label>
						<Form.Item name="notes">
							<TextArea
								placeholder="Введите комментарий"
								autoSize={{
									minRows: 4,
									maxRows: 10,
								}}
							/>
						</Form.Item>
					</div>
					<Row gutter={8}>
						<Col span={12}>
							<label className="form-label">Город</label>
							<Form.Item name="city">
								<Input />
							</Form.Item>
						</Col>
						<Col span={12}>
							<label className="form-label">Статус заявки</label>
							<Form.Item name="status">
								<Select
									placeholder="Выберите статус заявки"
									style={{
										width: '100%',
									}}
									allowClear
									options={[
										{
											value: 1,
											label: 'Новая',
										},
										{
											value: 2,
											label: 'В работе',
										},
										{
											value: 3,
											label: 'Отработана',
										},
										{
											value: 4,
											label: 'Спам',
										},
										{
											value: 5,
											label: 'Перезвонить',
										},
										{
											value: 6,
											label: 'Для обзвона',
										},
									]}
								/>
							</Form.Item>
							{/* <Select
								defaultValue="1"
								placeholder="Выберите статус заявки"
								style={{
									width: '100%',
								}}
								allowClear
								value={selectedModalStatus || null}
								onChange={handleStatusChange}
								options={[
									{
										value: '1',
										label: 'Новая',
									},
									{
										value: '2',
										label: 'В работе',
									},
									{
										value: '3',
										label: 'Отработана',
									},
									{
										value: '4',
										label: 'Спам',
									},
									{
										value: '5',
										label: 'Перезвонить',
									},
									{
										value: '6',
										label: 'Для обзвона',
									},
								]}
							/> */}
						</Col>
					</Row>
					<div className="text-end">
						<Form.Item noStyle={true}>
							<Button type="primary" htmlType="submit">
								Сохранить
							</Button>
						</Form.Item>
					</div>
				</Form>
			</Modal>
		</>
	);
};

export default ServiceRequestsTable;
